import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { bool } from 'prop-types';

import { useConfiguration } from '../../context/configurationContext';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { Page, NamedLink, PrimaryButton, SecondaryButton } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import css from './MarketingPage.module.css';

const MarketingPageComponent = props => {
  const { scrollingDisabled } = props;
  const config = useConfiguration();
  const marketplaceName = config.marketplaceName;

  const title = `Spark Greatness - Find Expert Mentors | ${marketplaceName}`;
  const schemaTitle = `Connect with Expert Mentors | ${marketplaceName}`;
  const description = `Connect with industry experts and accelerate your growth. Find mentors, book 1-on-1 sessions, and unlock your potential with ${marketplaceName}.`;

  return (
    <Page
      title={title}
      scrollingDisabled={scrollingDisabled}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        name: schemaTitle,
        description,
      }}
    >
      <div className={css.root}>
        <TopbarContainer />

        {/* Hero Section */}
        <section className={css.heroSection}>
          {/* Corner photo ellipses */}
          <div className={css.photoEllipse} data-corner="top-left">
            <img src="/static/images/violinist.jpg" alt="Professional violinist" />
          </div>
          <div className={css.photoEllipse} data-corner="top-right">
            <img src="/static/images/guitarist.jpg" alt="Professional guitarist" />
          </div>
          <div className={css.photoEllipse} data-corner="bottom-left">
            <img src="/static/images/student-sitting-on-shelf.jpg" alt="Student learning" />
          </div>
          <div className={css.photoEllipse} data-corner="bottom-right">
            <img src="/static/images/flowers-girl-for-some-reason.jpg" alt="Student with flowers" />
          </div>

          <div className={css.heroContainer}>
            <div className={css.heroContent}>
              <h1 className={css.heroTitle}>Spark Greatness.</h1>
              <p className={css.heroDescription}>
                Your music students have more career paths available than “international pop star.”
                Our mentors will guide them there.
              </p>
              <div className={css.heroButtons}>
                <a
                  href="https://calendly.com/ryankairalla/sonispark-partner-conference"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={css.primaryButton}
                >
                  <PrimaryButton>Bring Access To Your Students</PrimaryButton>
                </a>
                <NamedLink
                  name="SignupPage"
                  to="/signup?userType=mentor"
                  className={css.secondaryButton}
                >
                  <SecondaryButton>Become a Mentor</SecondaryButton>
                </NamedLink>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className={css.featuresSection}>
          <div className={css.container}>
            <h2 className={css.sectionTitle}>What Makes Sonispark Unique</h2>
            <div className={css.featuresGrid}>
              <div className={css.featureCard}>
                <div className={css.featureIcon}>
                  <img
                    src="/static/images/michel-catalisano-OZDmI-vO7Io-unsplash.jpg"
                    alt="Verified expertise"
                  />
                </div>
                <h3 className={css.featureTitle}>Verified Expertise</h3>
                <p className={css.featureDescription}>
                  All mentors are carefully vetted professionals with proven track records in their
                  fields. Get guidance from those who've been there.
                </p>
              </div>
              <div className={css.featureCard}>
                <div className={css.featureIcon}>
                  <img
                    src="/static/images/rob-simmons-yYh5hf9atNw-unsplash.jpg"
                    alt="Diverse career paths"
                  />
                </div>
                <h3 className={css.featureTitle}>Diverse Career Paths</h3>
                <p className={css.featureDescription}>
                  99% of the money that will ever be earned in the music industry is made outside
                  the spotlight. Our mentors represent the full spectrum of those diverse career
                  paths.
                </p>
              </div>
              <div className={css.featureCard}>
                <div className={css.featureIcon}>
                  <img
                    src="/static/images/wonderlane-6zlgM-GUd6I-unsplash.jpg"
                    alt="No cost to students or institution"
                  />
                </div>
                <h3 className={css.featureTitle}>No Cost To Students or Institution</h3>
                <p className={css.featureDescription}>
                  We and our mentors want to give the experience we wished had existed when we were
                  students. No cost to you or your students.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className={css.howItWorksSection}>
          <div className={css.container}>
            <h2 className={css.sectionTitle}>How It Works</h2>
            <div className={css.stepsGrid}>
              <div className={css.step}>
                <div className={css.stepIcon}>
                  <img
                    src="/static/images/david-hahn-bB7GmZRFavs-unsplash.jpg"
                    alt="Students sign up"
                  />
                </div>
                <h3 className={css.stepTitle}>Students Sign Up Free</h3>
                <p className={css.stepDescription}>
                  Students can register with their .edu email address (or an allowlist you specify)
                  and begin browsing our mentor directory.
                </p>
              </div>
              <div className={css.step}>
                <div className={css.stepIcon}>
                  <img src="/static/images/windows-cF32MfGaqP0-unsplash.jpg" alt="Easy booking" />
                </div>
                <h3 className={css.stepTitle}>Easy Booking</h3>
                <p className={css.stepDescription}>
                  After discovering a mentor whose advice they seek, they can view available meeting
                  times and book them, with calendar sync.
                </p>
              </div>
              <div className={css.step}>
                <div className={css.stepIcon}>
                  <img
                    src="/static/images/linkedin-sales-solutions-6ie6OjshvWg-unsplash.jpg"
                    alt="Video meetings"
                  />
                </div>
                <h3 className={css.stepTitle}>Video Meetings & Ongoing Messaging</h3>
                <p className={css.stepDescription}>
                  A simple video meeting interface allows students to connect with mentors. They can
                  also message mentors for ongoing support and questions.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className={css.ctaSection}>
          <div className={css.container}>
            <div className={css.ctaContent}>
              <h2 className={css.ctaTitle}>Ready to Expand Your Students' Horizons?</h2>
              <p className={css.ctaDescription}>
                Give your music students access to professional mentors who can show them the full
                spectrum of music careers. From sound engineering to artist management, help them
                discover paths beyond the spotlight.
              </p>
              <a
                href="https://calendly.com/ryankairalla/sonispark-partner-conference"
                target="_blank"
                rel="noopener noreferrer"
                className={css.ctaButton}
              >
                <PrimaryButton>Bring Access To Your Students</PrimaryButton>
              </a>
            </div>
          </div>
        </section>

        <FooterContainer />
      </div>
    </Page>
  );
};



MarketingPageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
};

const mapStateToProps = state => {
  return {
    scrollingDisabled: isScrollingDisabled(state),
  };
};

const MarketingPage = compose(connect(mapStateToProps))(MarketingPageComponent);

export default MarketingPage;
